import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Pencil, Trash2, Plus, LogOut, FileText, Users, Mail, Mic } from "lucide-react";
import api, { clearAuthToken } from "@/lib/api";

const CATEGORIES = [
  "The Great Wealth Transfer",
  "Minority Entrepreneurship",
  "Financial Literacy",
  "AI Governance & Technology",
  "Veteran Advocacy",
  "Leadership & Communication",
];

function ArticleEditor({ article, onSave, onClose }) {
  const [form, setForm] = useState({
    title: article?.title || "",
    excerpt: article?.excerpt || "",
    body: article?.body || "",
    category: article?.category || CATEGORIES[0],
    featured_image: article?.featured_image || "",
    published: article?.published ?? true,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (article) {
        await api.put(`/articles/${article.id}`, form);
      } else {
        await api.post("/articles", form);
      }
      onSave();
    } catch (err) {
      alert("Error saving article");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label className="text-sm mb-1.5 block">Title *</Label>
        <Input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} data-testid="editor-title" className="rounded-sm" />
      </div>
      <div>
        <Label className="text-sm mb-1.5 block">Category</Label>
        <Select value={form.category} onValueChange={(v) => setForm((p) => ({ ...p, category: v }))}>
          <SelectTrigger data-testid="editor-category" className="rounded-sm"><SelectValue /></SelectTrigger>
          <SelectContent>{CATEGORIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}</SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-sm mb-1.5 block">Excerpt *</Label>
        <Textarea value={form.excerpt} onChange={(e) => setForm((p) => ({ ...p, excerpt: e.target.value }))} data-testid="editor-excerpt" className="rounded-sm min-h-[80px]" />
      </div>
      <div>
        <Label className="text-sm mb-1.5 block">Body *</Label>
        <Textarea value={form.body} onChange={(e) => setForm((p) => ({ ...p, body: e.target.value }))} data-testid="editor-body" className="rounded-sm min-h-[200px]" />
      </div>
      <div>
        <Label className="text-sm mb-1.5 block">Featured Image URL</Label>
        <Input value={form.featured_image} onChange={(e) => setForm((p) => ({ ...p, featured_image: e.target.value }))} data-testid="editor-image" className="rounded-sm" />
      </div>
      <div className="flex items-center gap-3">
        <Switch checked={form.published} onCheckedChange={(v) => setForm((p) => ({ ...p, published: v }))} data-testid="editor-published" />
        <Label className="text-sm">Published</Label>
      </div>
      <div className="flex gap-3 pt-2">
        <Button type="submit" disabled={saving} data-testid="editor-save" className="bg-[#C9A84C] text-[#1B2A4A] hover:bg-[#b59744] hover:text-white font-semibold rounded-sm">
          {saving ? "Saving..." : article ? "Update Article" : "Create Article"}
        </Button>
        <Button type="button" variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
      </div>
    </form>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [articles, setArticles] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      if (data.role !== "admin") throw new Error("Not admin");
      setUser(data);
    } catch (err) {
      console.error("Auth check failed:", err.message);
      clearAuthToken();
      sessionStorage.removeItem("admin_user");
      navigate("/admin/login");
    }
  }, [navigate]);

  const loadArticles = useCallback(async () => {
    try {
      const { data } = await api.get("/articles?published_only=false");
      setArticles(data);
    } catch (err) {
      console.error("Failed to load articles:", err.message);
    }
  }, []);

  const loadSubscribers = useCallback(async () => {
    try {
      const { data } = await api.get("/subscribers");
      setSubscribers(data);
    } catch (err) {
      console.error("Failed to load subscribers:", err.message);
    }
  }, []);

  const loadContacts = useCallback(async () => {
    try {
      const { data } = await api.get("/contact");
      setContacts(data);
    } catch (err) {
      console.error("Failed to load contacts:", err.message);
    }
  }, []);

  const loadBookings = useCallback(async () => {
    try {
      const { data } = await api.get("/speaking/bookings");
      setBookings(data);
    } catch (err) {
      console.error("Failed to load bookings:", err.message);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);
  
  useEffect(() => { 
    if (user) { 
      loadArticles(); 
      loadSubscribers(); 
      loadContacts(); 
      loadBookings(); 
    } 
  }, [user, loadArticles, loadSubscribers, loadContacts, loadBookings]);

  const handleDeleteArticle = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    try {
      await api.delete(`/articles/${id}`);
      loadArticles();
    } catch (err) {
      console.error("Failed to delete article:", err.message);
    }
  };

  const handleLogout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.error("Logout error:", err.message);
    }
    clearAuthToken();
    sessionStorage.removeItem("admin_user");
    navigate("/admin/login");
  };

  if (!user) return (
    <div className="min-h-screen flex items-center justify-center bg-[#E8EDF5]">
      <div className="h-8 w-8 border-2 border-[#C9A84C] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#E8EDF5]">
      {/* HEADER */}
      <header className="bg-[#1B2A4A] text-white px-4 md:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="font-heading font-bold text-lg" data-testid="admin-dashboard-title">CMS Dashboard</h1>
          <Badge className="bg-[#C9A84C] text-[#1B2A4A] rounded-sm hover:bg-[#C9A84C]">{user.name}</Badge>
        </div>
        <div className="flex items-center gap-3">
          <a href="/" className="text-sm text-white/60 hover:text-white">View Site</a>
          <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="admin-logout" className="text-white/60 hover:text-white">
            <LogOut className="h-4 w-4 mr-1" /> Logout
          </Button>
        </div>
      </header>

      {/* CONTENT */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <Tabs defaultValue="articles" className="w-full">
          <TabsList className="bg-white rounded-sm border border-[#1B2A4A]/10 h-auto p-1 mb-8">
            <TabsTrigger value="articles" data-testid="admin-tab-articles" className="rounded-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
              <FileText className="h-4 w-4 mr-1.5" /> Articles ({articles.length})
            </TabsTrigger>
            <TabsTrigger value="subscribers" data-testid="admin-tab-subscribers" className="rounded-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
              <Mail className="h-4 w-4 mr-1.5" /> Subscribers ({subscribers.length})
            </TabsTrigger>
            <TabsTrigger value="contacts" data-testid="admin-tab-contacts" className="rounded-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
              <Users className="h-4 w-4 mr-1.5" /> Contacts ({contacts.length})
            </TabsTrigger>
            <TabsTrigger value="bookings" data-testid="admin-tab-bookings" className="rounded-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
              <Mic className="h-4 w-4 mr-1.5" /> Bookings ({bookings.length})
            </TabsTrigger>
          </TabsList>

          {/* ARTICLES TAB */}
          <TabsContent value="articles">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-bold text-xl text-[#1B2A4A]">Articles</h2>
              <Button onClick={() => { setEditingArticle(null); setEditorOpen(true); }} data-testid="admin-new-article" className="bg-[#C9A84C] text-[#1B2A4A] hover:bg-[#b59744] hover:text-white rounded-sm">
                <Plus className="h-4 w-4 mr-1.5" /> New Article
              </Button>
            </div>
            <div className="space-y-3">
              {articles.map((a) => (
                <div key={a.id} className="bg-white rounded-sm border border-[#1B2A4A]/10 p-4 flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-sm text-[#1B2A4A] truncate">{a.title}</h3>
                      {!a.published && <Badge variant="secondary" className="text-xs rounded-sm bg-yellow-100 text-yellow-800">Draft</Badge>}
                    </div>
                    <p className="text-xs text-[#1B2A4A]/50">{a.category} &bull; {new Date(a.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => { setEditingArticle(a); setEditorOpen(true); }} data-testid={`admin-edit-${a.id}`} className="h-8 w-8">
                      <Pencil className="h-4 w-4 text-[#1B2A4A]/60" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDeleteArticle(a.id)} data-testid={`admin-delete-${a.id}`} className="h-8 w-8">
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
              {articles.length === 0 && <p className="text-sm text-[#1B2A4A]/50 text-center py-8">No articles yet.</p>}
            </div>
          </TabsContent>

          {/* SUBSCRIBERS TAB */}
          <TabsContent value="subscribers">
            <h2 className="font-heading font-bold text-xl text-[#1B2A4A] mb-6">Subscribers</h2>
            <div className="bg-white rounded-sm border border-[#1B2A4A]/10 overflow-hidden">
              <div className="grid grid-cols-4 gap-4 p-3 bg-[#E8EDF5] text-xs font-semibold text-[#1B2A4A]/60 uppercase tracking-wider">
                <span>Email</span><span>Name</span><span>List</span><span>Date</span>
              </div>
              {subscribers.map((s) => (
                <div key={s.id} className="grid grid-cols-4 gap-4 p-3 border-t border-[#1B2A4A]/5 text-sm text-[#1B2A4A]/70">
                  <span className="truncate">{s.email}</span>
                  <span>{s.name || "\u2014"}</span>
                  <Badge variant="secondary" className="text-xs rounded-sm self-center w-fit">{s.list_type}</Badge>
                  <span className="text-xs">{new Date(s.subscribed_at).toLocaleDateString()}</span>
                </div>
              ))}
              {subscribers.length === 0 && <p className="text-sm text-[#1B2A4A]/50 text-center py-8">No subscribers yet.</p>}
            </div>
          </TabsContent>

          {/* CONTACTS TAB */}
          <TabsContent value="contacts">
            <h2 className="font-heading font-bold text-xl text-[#1B2A4A] mb-6">Contact Submissions</h2>
            <div className="space-y-3">
              {contacts.map((c) => (
                <div key={c.id} className="bg-white rounded-sm border border-[#1B2A4A]/10 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="text-xs rounded-sm bg-[#C9A84C]/20 text-[#1B2A4A]">{c.inquiry_type}</Badge>
                    <span className="text-xs text-[#1B2A4A]/40">{new Date(c.submitted_at).toLocaleDateString()}</span>
                  </div>
                  <p className="text-sm font-semibold text-[#1B2A4A]">{c.name} {c.email && `(${c.email})`}</p>
                  {c.organization && <p className="text-xs text-[#1B2A4A]/50">{c.organization}</p>}
                  {c.message && <p className="text-sm text-[#1B2A4A]/70 mt-2">{c.message}</p>}
                </div>
              ))}
              {contacts.length === 0 && <p className="text-sm text-[#1B2A4A]/50 text-center py-8">No contact submissions yet.</p>}
            </div>
          </TabsContent>

          {/* BOOKINGS TAB */}
          <TabsContent value="bookings">
            <h2 className="font-heading font-bold text-xl text-[#1B2A4A] mb-6">Speaking Bookings</h2>
            <div className="space-y-3">
              {bookings.map((b) => (
                <div key={b.id} className="bg-white rounded-sm border border-[#1B2A4A]/10 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs text-[#1B2A4A]/40">{new Date(b.submitted_at).toLocaleDateString()}</span>
                    {b.format && <Badge variant="secondary" className="text-xs rounded-sm">{b.format}</Badge>}
                  </div>
                  <p className="text-sm font-semibold text-[#1B2A4A]">{b.name} {b.organization && `\u2014 ${b.organization}`}</p>
                  <p className="text-xs text-[#1B2A4A]/50">{b.event_name} {b.event_date && `| ${b.event_date}`} {b.location && `| ${b.location}`}</p>
                  {b.message && <p className="text-sm text-[#1B2A4A]/70 mt-2">{b.message}</p>}
                </div>
              ))}
              {bookings.length === 0 && <p className="text-sm text-[#1B2A4A]/50 text-center py-8">No booking requests yet.</p>}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* ARTICLE EDITOR DIALOG */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingArticle ? "Edit Article" : "New Article"}</DialogTitle>
          </DialogHeader>
          <ArticleEditor
            article={editingArticle}
            onSave={() => { setEditorOpen(false); loadArticles(); }}
            onClose={() => setEditorOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
